/**
 * LearningPanel component for chat-first learning.
 * Opens in the right side panel (like ResumePreviewPanel) when
 * AI detects a learning intent in chat.
 */

import { skillNameFromLearningTopicTitle } from '@/lib/learning-topic-recovery'
import { cn } from '@/lib/utils'
import type { LearningContentEvent, LearningTopicGeneratingEvent } from '@/types/learning'
import {
  AlertCircle,
  ArrowRight,
  BookOpen,
  CheckCircle2,
  GraduationCap,
  Loader2,
  X,
} from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { Link } from 'react-router-dom'
import remarkGfm from 'remark-gfm'
import { Button } from './ui/button'

interface LearningPanelProps {
  content: LearningContentEvent | null
  isLoading: boolean
  error: string | null
  topicGenerating: LearningTopicGeneratingEvent | null
  onClose: () => void
  onMarkComplete?: () => void
  onContinueNext?: () => void
  isLoadingNext?: boolean
  className?: string
}

export function LearningPanel({
  content,
  isLoading,
  error,
  topicGenerating,
  onClose,
  onMarkComplete,
  onContinueNext,
  isLoadingNext = false,
  className,
}: LearningPanelProps) {
  const isComplete = content?.progress?.status === 'completed'
  const progressPercent = content?.progress?.progress_percent ?? 0
  const hasNextModule =
    content != null && content.total_modules > 0 && content.order_index + 1 < content.total_modules
  const generatingTopicId = topicGenerating?.topic_id ?? content?.generating_topic_id ?? null
  const generatingLabel =
    topicGenerating?.skill_name || topicGenerating?.title || content?.topic_title || 'your skill'
  // Align header with the "Learn" nav module: strip the API's "Learning:" prefix
  const rawTitle = topicGenerating?.title || content?.topic_title || ''
  const skillName = rawTitle ? skillNameFromLearningTopicTitle(rawTitle) : null
  const headerTitle = skillName ? `Learn: ${skillName}` : rawTitle || 'Learn'

  return (
    <div className={cn('h-full flex flex-col bg-muted relative', className)}>
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 z-10 overflow-hidden">
          <div className="absolute inset-0 bg-card/80 backdrop-blur-[2px]" />

          <div
            className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-status-500 to-transparent opacity-60"
            style={{ animation: 'scan 2s ease-in-out infinite' }}
          />

          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative">
              <div
                className="absolute inset-0 rounded-full border-2 border-status-500/30"
                style={{
                  width: '80px',
                  height: '80px',
                  left: '-20px',
                  top: '-20px',
                  animation: 'pulse-ring 1.5s ease-out infinite',
                }}
              />
              <div
                className="absolute inset-0 rounded-full border-2 border-status-500/20"
                style={{
                  width: '80px',
                  height: '80px',
                  left: '-20px',
                  top: '-20px',
                  animation: 'pulse-ring 1.5s ease-out infinite 0.5s',
                }}
              />
              <div className="w-10 h-10 rounded-full bg-status-500 flex items-center justify-center shadow-lg shadow-brand-500/30">
                <svg
                  className="w-5 h-5 text-white"
                  style={{ animation: 'spin 2s linear infinite' }}
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <style>{`
            @keyframes scan {
              0% { top: 0%; opacity: 0; }
              10% { opacity: 0.6; }
              90% { opacity: 0.6; }
              100% { top: 100%; opacity: 0; }
            }
            @keyframes pulse-ring {
              0% { transform: scale(0.8); opacity: 0.8; }
              100% { transform: scale(1.5); opacity: 0; }
            }
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}

      {/* Header */}
      <div className="p-4 border-b bg-card flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <GraduationCap className="h-5 w-5 text-status-600 shrink-0" />
          <div className="min-w-0">
            <h3 className="font-medium text-foreground truncate">{headerTitle}</h3>
            {content && content.total_modules > 0 && (
              <p className="text-xs text-muted-foreground">
                Module {content.order_index + 1} of {content.total_modules}
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 hover:bg-muted rounded-md transition-colors shrink-0"
          title="Close panel"
        >
          <X className="h-4 w-4 text-muted-foreground" />
        </button>
      </div>

      {/* Module title bar */}
      {content?.module_title && !error && (
        <div className="px-4 py-2 bg-status-50 border-b border-status-100 text-sm">
          <span className="text-status-700 font-medium">{content.module_title}</span>
        </div>
      )}

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto">
        {content?.content ? (
          <div className="p-5 prose prose-sm max-w-none prose-headings:text-foreground prose-p:text-foreground/80 prose-li:text-foreground/80 prose-strong:text-foreground">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content.content}</ReactMarkdown>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <AlertCircle className="h-12 w-12 text-danger-400 mb-4" />
            <p className="text-foreground/80 font-medium">Could not load learning content</p>
            <p className="text-sm text-muted-foreground mt-2 max-w-sm">{error}</p>
            <Button asChild variant="outline" className="mt-6">
              <Link to="/learn">Go to Learn</Link>
            </Button>
          </div>
        ) : generatingTopicId ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <Loader2 className="h-12 w-12 text-status-500 mb-4 animate-spin" />
            <p className="text-foreground/80 font-medium">Creating your learning path</p>
            <p className="text-sm text-muted-foreground mt-2 max-w-sm">
              Building modules for{' '}
              <span className="font-medium text-foreground/80">{generatingLabel}</span>. This
              usually takes a minute or two.
            </p>
            <Button asChild className="mt-6 bg-brand-600 hover:bg-brand-700">
              <Link to={`/learn/${generatingTopicId}`}>View on Learn page</Link>
            </Button>
          </div>
        ) : !isLoading ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <BookOpen className="h-12 w-12 text-gray-300 mb-4" />
            <p className="text-muted-foreground">Ask the AI to teach you something</p>
            <p className="text-sm text-muted-foreground mt-2">
              Try "teach me about..." in the chat
            </p>
          </div>
        ) : null}
      </div>

      {/* Footer with progress */}
      {content?.module_id && !error && (
        <div className="p-4 border-t bg-card shrink-0 space-y-3">
          {/* Progress bar */}
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className={cn(
                'h-2 rounded-full transition-all duration-500',
                isComplete ? 'bg-status-500' : 'bg-status-500',
              )}
              style={{ width: `${Math.max(progressPercent, 5)}%` }}
            />
          </div>

          <Button
            onClick={onMarkComplete}
            disabled={isComplete}
            variant={isComplete ? 'outline' : 'default'}
            className={cn('w-full', !isComplete && 'bg-brand-600 hover:bg-brand-700')}
          >
            {isComplete ? (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4 text-brand-600" />
                Completed
              </>
            ) : (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Mark Complete
              </>
            )}
          </Button>

          {isComplete && hasNextModule && onContinueNext && (
            <Button
              onClick={onContinueNext}
              disabled={isLoadingNext}
              className="w-full bg-brand-600 hover:bg-brand-700"
            >
              {isLoadingNext ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading next module...
                </>
              ) : (
                <>
                  Continue to Module {content.order_index + 2}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
